// services/ChatService.js
class ChatService {
  constructor(isElectron, baseURL = '') {
    this.isElectron = isElectron;
    this.baseURL = baseURL;
    this.controller = null;
    this.isStreaming = false;
  }

  /**
   * 发送消息并接收流式响应
   * @param {Object} params - 请求参数
   * @param {string} params.message - 消息内容
   * @param {string} params.model - 模型名称
   * @param {string} params.userId - 用户ID
   * @param {Function} onChunk - 接收数据块的回调
   * @param {Function} onDone - 完成时的回调
   * @param {Function} onError - 错误处理的回调
   * @returns {Promise<void>}
   */
  async sendMessage({ message, model, userId }, onChunk, onDone, onError) {
    // 如果已有进行中的请求，先中断
    if (this.controller) {
      this.abort();
    }

    this.controller = new AbortController();
    this.isStreaming = true;
    
    try {
      const response = await fetch(`${this.baseURL}/user/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Origin': this.getOrigin(), // 动态设置Origin
        },
        body: JSON.stringify({ 
          message, 
          model, 
          user_id: userId 
        }),
        signal: this.controller.signal,
        credentials: 'omit', // 不发送cookies
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser');
      }

      // 创建流式读取器
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      
      // 读取流数据
      while (true) {
        if (!this.isStreaming) break;
        
        const { done, value } = await reader.read();
        
        if (done) {
          // 处理缓冲区中剩余的数据
          if (buffer.trim()) {
            this._processSSEBuffer(buffer, onChunk);
          }
          break;
        }

        // 解码数据块并添加到缓冲区
        buffer += decoder.decode(value, { stream: true });
        
        // 处理缓冲区中的完整事件
        const { remainingBuffer, events } = this._extractSSEEvents(buffer);
        buffer = remainingBuffer;
        
        // 处理每个事件
        for (const event of events) {
          if (event.type === 'message') {
            if (event.data === '[DONE]') {
              onDone?.();
              continue;
            }
            onChunk?.(event.data);
          } else if (event.type === 'error') {
            throw new Error(event.data || 'Server error');
          }
        }
      }
      
      onDone?.();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
      } else {
        console.error('Stream error:', error);
        onError?.(error);
      }
    } finally {
      this.isStreaming = false;
      this.controller = null;
    }
  }

  /**
   * 获取当前Origin
   */
  getOrigin() {
    // 如果是Electron环境
    // console.log("origin-> ", this.isElectron);
    
    if (this.isElectron) {
      return 'file://';
    }
    // 如果是Web环境
    return window.location.origin;
  }

  /**
   * 从缓冲区提取SSE事件
   * @param {string} buffer - 缓冲区数据
   * @returns {Object} 包含剩余缓冲区和处理后的事件
   */
  _extractSSEEvents(buffer) {
    const events = [];
    let lastIndex = 0;
    
    // SSE格式: data: {data}\n\n
    const ssePattern = /(?:^|\n)(?:(event|data|id|retry):[^\n]*\n)*\n/g;
    let match;
    
    while ((match = ssePattern.exec(buffer)) !== null) {
      const eventText = match[0].trim();
      if (!eventText) continue;
      
      const lines = eventText.split('\n');
      const event = { type: 'message', data: '' };
      
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          event.type = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          event.data += line.slice(6);
        } else if (line.startsWith('id: ')) {
          event.id = line.slice(4).trim();
        } else if (line.startsWith('retry: ')) {
          event.retry = parseInt(line.slice(7).trim(), 10);
        }
      }
      
      events.push(event);
      lastIndex = match.index + match[0].length;
    }
    
    return {
      remainingBuffer: buffer.slice(lastIndex),
      events
    };
  }

  /**
   * 处理SSE缓冲区
   * @param {string} buffer - 缓冲区数据
   * @param {Function} onChunk - 数据回调
   */
  _processSSEBuffer(buffer, onChunk) {
    const lines = buffer.split('\n');
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim();
        if (data && data !== '[DONE]') {
          onChunk?.(data);
        }
      }
    }
  }

  /**
   * 中断当前请求
   */
  abort() {
    if (this.controller) {
      this.controller.abort();
    }
    this.isStreaming = false;
    this.controller = null;
  }

  /**
   * 检查是否正在请求中
   * @returns {boolean}
   */
  isRequesting() {
    return this.isStreaming || !!this.controller;
  }
}

export default ChatService;